import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';

import {
    clearLabcodeStaffSession,
    getLabcodeStaffProfile
} from '../../lib/labcode-staff-session';
import {platformGetMe} from '../../lib/labcode-platform-api';

import {PlatformNetworksView} from './platform-portal-views.jsx';

import styles from '../staff-portal/staff-portal.css';

function parsePlatformRoute () {
    const raw = (typeof window !== 'undefined' && window.location.hash)
        ? window.location.hash.replace(/^#/, '')
        : '';
    const parts = raw.split('/').filter(Boolean);
    if (parts[0] !== 'platform') return 'dashboard';
    const page = parts[1] || 'dashboard';
    if (['dashboard', 'editor'].includes(page)) return page;
    return 'dashboard';
}

function setPlatformRoute (page) {
    window.location.hash = `platform/${page}`;
}

/**
 * Painel do administrador geral Labcode: criação de redes e responsáveis org_admin.
 */
const PlatformPortal = ({children: editorPanel}) => {
    const [route, setRoute] = useState(() => parsePlatformRoute());
    const [jwtProfile] = useState(() => getLabcodeStaffProfile());
    const [detail, setDetail] = useState(null);
    const [meError, setMeError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { staff } = await platformGetMe();
                if (!cancelled) {
                    setDetail(staff);
                    setMeError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setMeError(e && e.message ? e.message : 'Erro ao carregar perfil.');
                    setDetail(null);
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onHash = () => setRoute(parsePlatformRoute());
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const handleLogout = useCallback(() => {
        clearLabcodeStaffSession();
        const h = window.location.hash || '';
        if (h.startsWith('#platform') || h.startsWith('#staff')) {
            window.location.hash = '';
        }
    }, []);

    const profile = detail || jwtProfile;

    const go = useCallback(page => {
        setPlatformRoute(page);
    }, []);

    const navBtn = (id, label) => (
        <button
            key={id}
            className={
                route === id
                    ? `${styles.navBtn} ${styles.navBtnActive}`
                    : styles.navBtn
            }
            type="button"
            onClick={() => go(id)}
        >
            {label}
        </button>
    );

    return (
        <div className={styles.shell}>
            <header className={styles.topBar}>
                <p className={styles.brand}>
                    {'Labcode · Plataforma'}
                </p>
                <span className={styles.userMeta}>
                    {profile && profile.email ? profile.email : ''}
                </span>
                <button
                    className={styles.logoutBtn}
                    type="button"
                    onClick={handleLogout}
                >
                    {'Sair'}
                </button>
            </header>
            <div className={styles.body}>
                <nav
                    aria-label="Administração da plataforma"
                    className={styles.sideNav}
                >
                    {navBtn('dashboard', 'Redes')}
                    {navBtn('editor', 'Editor Labcode')}
                </nav>
                <main
                    className={route === 'editor' ? styles.mainEditor : styles.main}
                >
                    {route === 'editor' ? (
                        <div className={styles.editorFill}>
                            {editorPanel}
                        </div>
                    ) : (
                        <>
                            {meError ? (
                                <p
                                    className={styles.errorText}
                                    role="alert"
                                >
                                    {meError}
                                </p>
                            ) : null}
                            <div className={styles.hintBox}>
                                <p>
                                    <strong>
                                        {profile && profile.fullName ? profile.fullName : '—'}
                                    </strong>
                                    {' · '}
                                    <strong>
                                        {'Administrador geral do Labcode'}
                                    </strong>
                                    <br />
                                    {
                                        'Gira todas as redes; não está vinculado a uma organização escolar.'
                                    }
                                </p>
                            </div>
                            <PlatformNetworksView />
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

PlatformPortal.propTypes = {
    children: PropTypes.node
};

PlatformPortal.defaultProps = {
    children: null
};

export default PlatformPortal;
